import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '@/components/ui/UnsavedChangesModal';
import { DoaResponsibilitiesPanel } from '@/pages/studies/DoaResponsibilitiesPanel';
import { AdverseEventConfigPanel } from '@/pages/studies/AdverseEventConfigPanel';
import { PageHeader } from '@/components/layout/PageHeader';
import { encryptId, resolveId } from '@/lib/encryptId';
import {
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Plus,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  X,
  Users,
  Pencil,
  Check,
  Info,
  FileText,
} from 'lucide-react';
import type { STUDY_STAFF_ROLES } from '@/api/client';
import {
  onboarding,
  studies,
  studiesCreate,
  studyStaff,
  protocol,
  consent,
  listActiveBinderTemplates,
  isBlindedStudyType,
  type OnboardingStepNumber,
  type ReadinessFlagKey,
  type VisitTemplateEntry,
  type OnboardingContext,
  type Step2Payload,
  type DerivedFlag,
  type VisitCategory,
  type StudyBlindingType,
} from '@/api/client';
import { fmtMoney, fmtCentsInput, parseMoneyToCents } from '@/lib/money';

const ASSIGNABLE_STAFF_ROLES = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
] as const satisfies readonly (typeof STUDY_STAFF_ROLES)[number][];

import { useAuth } from '@/contexts/AuthContext';
import { StudyDetailsPanel } from '@/pages/studies/StudyDetailsPanel';
import { humanRole, humanReadiness, humanStatus, studyStatusBadgeClass } from '@/lib/humanise';
import { toast } from 'sonner';
import { WizardShell } from '@/components/ui/WizardShell';
import { deriveStepStatuses, type StepDef } from '@/components/ui/Stepper';
import { AuditTrailPanel } from '@/components/audit/AuditTrailPanel';
import { ProtocolUploadCard } from '@/pages/studies/onboarding/ProtocolUploadCard';
import { ConsentUploadCard } from '@/pages/studies/onboarding/ConsentUploadCard';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * DREAM-18 — Study Onboarding 5-Step Wizard.
 *
 * Two entry points:
 *   /studies/new          -> create a draft study, then enter the wizard
 *   /studies/:id/onboarding -> resume the wizard for an existing study
 *
 * 3-week cut: steps 1-3 are fully functional; steps 4-5 are stubs:
 *   step 4 (eSignature) auto-completes on next-click (DocuSeal integration deferred)
 *   step 5 (Activation) shows the activation-gate checklist; "Activate" calls the BE
 */

const STEP_DEFS: { key: string; label: string; step: OnboardingStepNumber }[] = [
  { key: 'step1', label: '1. Study Invitation', step: 1 },
  { key: 'step2', label: '2. Protocol Setup', step: 2 },
  { key: 'step3', label: '3. Study Customisation', step: 3 },
  { key: 'step4', label: '4. eSignature Approval', step: 4 },
  { key: 'step5', label: '5. Activation', step: 5 },
];

const STEP_NAMES: Record<OnboardingStepNumber, string> = {
  1: 'Study Invitation',
  2: 'Protocol Setup',
  3: 'Study Customisation',
  4: 'eSignature Approval',
  5: 'Activation',
};

export function StudyOnboardingWizardPage() {
  const { id: encryptedIdParam } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  // Decrypt the URL param — gracefully falls back for any raw UUID still in
  // browser history via resolveId.
  const studyIdParam = encryptedIdParam ? resolveId(encryptedIdParam) : undefined;

  // If no studyId param, render the "create study" flow first.
  if (!studyIdParam)
    return (
      <CreateStudyForm
        onCreated={(id) => navigate(`/studies/${encryptId(id)}/onboarding`, { replace: true })}
        onCancel={() => navigate('/studies')}
      />
    );

  return <OnboardingWizard studyId={studyIdParam} />;
}

// ---------------------------------------------------------------------------
// Create-study form (entry for /studies/new)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Sponsor combobox — searchable dropdown with "add new" option
// ---------------------------------------------------------------------------

function SponsorCombobox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const sponsorsQ = useQuery({
    queryKey: ['studies-sponsors'],
    queryFn: () => studies.listSponsors(),
    staleTime: 60_000,
  });

  const saveSponsor = useMutation({
    mutationFn: (name: string) => studies.createSponsor(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['studies-sponsors'] });
    },
    onError: () => toast.error('Failed to save sponsor'),
  });

  const list = sponsorsQ.data?.sponsors ?? [];
  const filtered = search.trim()
    ? list.filter((s) => s.toLowerCase().includes(search.toLowerCase()))
    : list;
  const exactMatch = list.some((s) => s.toLowerCase() === search.toLowerCase().trim());

  // Close on outside click
  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  function selectExisting(sponsor: string) {
    onChange(sponsor);
    setOpen(false);
    setSearch('');
  }

  function confirmNewSponsor(name: string) {
    const trimmed = name.trim();
    if (trimmed.length < 2) return;
    onChange(trimmed);
    setOpen(false);
    setSearch('');
    setAddingNew(false);
    setNewName('');
    // Persist to ctms_sponsors immediately. On failure, roll back the form value.
    saveSponsor.mutate(trimmed, {
      onError: () => onChange(''),
    });
  }

  if (addingNew) {
    return (
      <div className="flex gap-2">
        <input
          autoFocus
          className="input flex-1"
          placeholder="Enter new sponsor name"
          value={newName}
          maxLength={255}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirmNewSponsor(newName);
            }
            if (e.key === 'Escape') {
              setAddingNew(false);
              setNewName('');
            }
          }}
        />
        <button
          type="button"
          className="btn-primary text-xs px-3"
          disabled={newName.trim().length < 2 || saveSponsor.isPending}
          onClick={() => confirmNewSponsor(newName)}
        >
          {saveSponsor.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          className="btn-ghost text-xs px-3"
          onClick={() => {
            setAddingNew(false);
            setNewName('');
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        className="input w-full flex items-center justify-between text-left"
        onClick={() => {
          setOpen((o) => !o);
          setSearch('');
        }}
      >
        <span className={value ? 'text-ink-primary' : 'text-ink-muted/60'}>
          {value || 'Select or add sponsor…'}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-surface-border bg-white shadow-lg">
          <div className="p-2 border-b border-surface-border">
            <input
              autoFocus
              className="input text-sm w-full"
              placeholder="Search sponsors…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
              }}
            />
          </div>
          <ul className="max-h-48 overflow-y-auto py-1">
            {sponsorsQ.isLoading && (
              <li className="px-3 py-2 text-xs text-ink-muted flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </li>
            )}
            {!sponsorsQ.isLoading && filtered.length === 0 && !search.trim() && (
              <li className="px-3 py-2 text-xs text-ink-muted">No sponsors yet.</li>
            )}
            {filtered.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm text-left hover:bg-surface-muted text-ink-primary"
                  onClick={() => selectExisting(s)}
                >
                  {s}
                </button>
              </li>
            ))}
            {!exactMatch && search.trim().length >= 2 && (
              <li>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm text-left hover:bg-surface-muted text-navy-600 font-medium border-t border-surface-border"
                  onClick={() => confirmNewSponsor(search.trim())}
                >
                  + Add "{search.trim()}" as new sponsor
                </button>
              </li>
            )}
            {!search.trim() && (
              <li>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-sm text-left hover:bg-surface-muted text-navy-600 font-medium border-t border-surface-border"
                  onClick={() => {
                    setOpen(false);
                    setAddingNew(true);
                  }}
                >
                  + Add new sponsor
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function CreateStudyForm({
  onCreated,
  onCancel,
}: {
  onCreated: (studyId: string) => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState({
    // protocol_number is server-minted (STD-YYYY-####) — no user input.
    // See [[auto-generate-identifiers]].
    title: '',
    sponsor: '',
    // Punch-list f9 — free-text identifier the sponsor uses for this
    // study in their own systems. Purely informational, never minted.
    sponsor_study_id: '',
    // Punch-list DT2-BLIND-1 — trial blinding design.
    blinding_type: '' as '' | StudyBlindingType,
    phase: '',
    therapeutic_area: '',
    planned_start_date: '',
    planned_end_date: '',
    enrollment_target: 0,
    enrollment_target_site: 0,
    // F8c - eISF binder templates. Lock in once the wizard hits activate (FR-ISF-5).
    binder_template_id: '',
    unblinded_binder_template_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isBlinded = isBlindedStudyType(draft.blinding_type || null);

  // F8c - Populate the binder-template dropdowns; preselect each scope's default row.
  const bindersQ = useQuery({
    queryKey: ['binderTemplates', 'active'],
    queryFn: () => listActiveBinderTemplates(),
    staleTime: 5 * 60_000,
  });
  const templates = bindersQ.data?.templates ?? [];
  const blindedTemplates = templates.filter((t) => t.blinding_scope === 'blinded');
  const unblindedTemplates = templates.filter((t) => t.blinding_scope === 'unblinded');
  useEffect(() => {
    const defaultFor = (scope: 'blinded' | 'unblinded') =>
      (bindersQ.data?.templates ?? []).find(
        (t) => t.blinding_scope === scope && t.is_default === 'true',
      )?.template_id ?? '';
    setDraft((d) => ({
      ...d,
      binder_template_id: d.binder_template_id || defaultFor('blinded'),
      unblinded_binder_template_id: isBlinded
        ? d.unblinded_binder_template_id || defaultFor('unblinded')
        : '',
    }));
  }, [bindersQ.data, isBlinded]);

  // Phase 2 Defect 2.5 — guard against silent data loss on navigation.
  // Dirtiness is derived from field content only; the guard resets itself
  // via markSaved() (see hook doc) in the mutation's onSuccess, which fires
  // synchronously before the redirect and prevents the false-positive
  // "Leave without saving?" prompt after a successful save.
  const isDirty =
    draft.title.trim() !== '' ||
    draft.sponsor.trim() !== '' ||
    draft.sponsor_study_id.trim() !== '' ||
    draft.phase !== '' ||
    draft.therapeutic_area.trim() !== '' ||
    draft.planned_start_date !== '' ||
    draft.planned_end_date !== '' ||
    draft.enrollment_target !== 0 ||
    draft.enrollment_target_site !== 0;
  const guard = useUnsavedChangesGuard(isDirty);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!draft.title.trim()) errs.title = 'Title is required.';
    else if (draft.title.trim().length < 2) errs.title = 'Must be at least 2 characters.';
    if (!draft.sponsor.trim()) errs.sponsor = 'Sponsor is required.';
    if (
      draft.planned_start_date &&
      draft.planned_end_date &&
      draft.planned_end_date < draft.planned_start_date
    ) {
      errs.planned_end_date = 'End date must be after start date.';
    }
    if (draft.enrollment_target < 0) errs.enrollment_target = 'Must be 0 or greater.';
    if (
      draft.enrollment_target > 0 &&
      draft.enrollment_target_site > 0 &&
      draft.enrollment_target_site > draft.enrollment_target
    ) {
      errs.enrollment_target_site = 'Site target cannot exceed the global target.';
    }
    if (!draft.binder_template_id) {
      errs.binder_template_id = isBlinded
        ? 'Blinded binder template is required.'
        : 'Study binder template is required.';
    }
    if (isBlinded && !draft.unblinded_binder_template_id) {
      errs.unblinded_binder_template_id = 'Unblinded binder template is required.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function clearError(field: string) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  const create = useMutation({
    mutationFn: () =>
      studiesCreate({
        // protocol_number omitted — BE mints STD-YYYY-####.
        title: draft.title.trim(),
        sponsor: draft.sponsor.trim(),
        sponsor_study_id: draft.sponsor_study_id.trim() || undefined,
        blinding_type: draft.blinding_type || undefined,
        phase: draft.phase || undefined,
        therapeutic_area: draft.therapeutic_area.trim() || undefined,
        planned_start_date: draft.planned_start_date || undefined,
        planned_end_date: draft.planned_end_date || undefined,
        enrollment_target: draft.enrollment_target,
        enrollment_target_site: draft.enrollment_target_site || undefined,
        binder_template_id: draft.binder_template_id,
        ...(isBlinded ? { unblinded_binder_template_id: draft.unblinded_binder_template_id } : {}),
      }),
    onSuccess: (res) => {
      // Release the unsaved-changes guard BEFORE the redirect so the
      // subsequent navigate() call is not intercepted. markSaved() flips a
      // ref synchronously — the isDirty-based state fallback would race the
      // navigate() call and re-arm the modal on a successful save.
      guard.markSaved();
      toast.success(`Study ${res.study.protocol_number} created — onboarding wizard ready`);
      onCreated(res.study_id);
    },
    onError: (err) => toast.error(errorText(err, 'Failed to create study')),
  });

  function FieldError({ field }: { field: string }) {
    return errors[field] ? <p className="mt-1 text-xs text-danger-500">{errors[field]}</p> : null;
  }

  /**
   * CR-2026-09-29 — warn when the chosen binder template has not routed every
   * mandatory document to one of its folders. Uploads on a study using it
   * would not file into the binder, and the binder would look complete while
   * missing the protocol or the consent. A platform admin fixes it in
   * Admin, Binder Templates.
   */
  function TemplateReadinessNote({
    template,
  }: {
    template?: { readiness?: { ready: boolean; missing: Array<{ name: string }> } };
  }) {
    const missing = template?.readiness && !template.readiness.ready
      ? template.readiness.missing
      : null;
    if (!missing || missing.length === 0) return null;
    return (
      <p className="mt-1 text-xs text-amber-700">
        This template has no folder set up for {missing.map((m) => m.name).join(', ')}. Those
        uploads will not file into the binder until an admin maps them.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <UnsavedChangesModal
        guard={guard}
        title="Leave without creating this study?"
        body="You have unsaved study details on this page (title, sponsor, or other fields). If you leave now, those edits will be lost."
        proceedLabel="Discard & leave"
        stayLabel="Continue editing"
      />
      <PageHeader
        backTo="/studies"
        title="New study"
        subtitle="Capture the core protocol details. The 5-step onboarding wizard takes over after this."
      />
      <div className="mx-auto max-w-2xl">
      <form
        className="card space-y-5 p-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (validate()) create.mutate();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="label text-xs" htmlFor="title">
              Title *
            </label>
            <input
              id="title"
              maxLength={255}
              className={`input ${errors.title ? 'border-danger-500' : ''}`}
              value={draft.title}
              onChange={(e) => {
                setDraft({ ...draft, title: e.target.value });
                clearError('title');
              }}
            />
            <FieldError field="title" />
          </div>
          <div>
            <label className="label text-xs">Sponsor *</label>
            <SponsorCombobox
              value={draft.sponsor}
              onChange={(v) => {
                setDraft({ ...draft, sponsor: v });
                clearError('sponsor');
              }}
            />
            <FieldError field="sponsor" />
          </div>
          <div>
            <label className="label text-xs" htmlFor="sponsor_study_id">
              Sponsor Study ID (optional)
            </label>
            <input
              id="sponsor_study_id"
              maxLength={120}
              className="input"
              value={draft.sponsor_study_id}
              onChange={(e) => setDraft({ ...draft, sponsor_study_id: e.target.value })}
              placeholder="Sponsor's own identifier for this study"
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="phase">
              Phase
            </label>
            <select
              id="phase"
              className="input"
              value={draft.phase}
              onChange={(e) => setDraft({ ...draft, phase: e.target.value })}
            >
              <option value="">— Select phase —</option>
              <option value="Phase 1">Phase 1</option>
              <option value="Phase 2">Phase 2</option>
              <option value="Phase 3">Phase 3</option>
              <option value="Phase 4">Phase 4</option>
            </select>
          </div>
          <div>
            <label className="label text-xs" htmlFor="blinding_type">
              Blinding design
            </label>
            <select
              id="blinding_type"
              className="input"
              value={draft.blinding_type}
              onChange={(e) => {
                setDraft({ ...draft, blinding_type: e.target.value as '' | StudyBlindingType });
                clearError('binder_template_id');
                clearError('unblinded_binder_template_id');
              }}
            >
              <option value="">— Select blinding design —</option>
              <option value="open_label">Open-label</option>
              <option value="single_blind">Single-blind</option>
              <option value="double_blind">Double-blind</option>
              <option value="triple_blind">Triple-blind</option>
            </select>
          </div>
          <div>
            <label className="label text-xs" htmlFor="ta">
              Therapeutic area
            </label>
            <input
              id="ta"
              className="input"
              value={draft.therapeutic_area}
              onChange={(e) => setDraft({ ...draft, therapeutic_area: e.target.value })}
              placeholder="Oncology"
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="target">
              Enrollment target{' '}
              <span className="text-ink-muted font-normal">(global — all sites)</span>
            </label>
            <input
              id="target"
              type="number"
              min={0}
              max={100000}
              className={`input ${errors.enrollment_target ? 'border-danger-500' : ''}`}
              value={draft.enrollment_target}
              onChange={(e) => {
                setDraft({ ...draft, enrollment_target: parseInt(e.target.value || '0', 10) });
                clearError('enrollment_target');
              }}
            />
            <FieldError field="enrollment_target" />
          </div>
          <div>
            <label className="label text-xs" htmlFor="site-target">
              Enrollment target <span className="text-ink-muted font-normal">(this site)</span>
            </label>
            <input
              id="site-target"
              type="number"
              min={0}
              max={100000}
              className={`input ${errors.enrollment_target_site ? 'border-danger-500' : ''}`}
              value={draft.enrollment_target_site}
              placeholder="Defaults to global target if blank"
              onChange={(e) => {
                setDraft({ ...draft, enrollment_target_site: parseInt(e.target.value || '0', 10) });
                clearError('enrollment_target_site');
              }}
            />
            <FieldError field="enrollment_target_site" />
          </div>
          <div>
            <label className="label text-xs" htmlFor="start">
              Planned start
            </label>
            <input
              id="start"
              type="date"
              className="input"
              value={draft.planned_start_date}
              onChange={(e) => {
                setDraft({ ...draft, planned_start_date: e.target.value });
                clearError('planned_end_date');
              }}
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="end">
              Planned end
            </label>
            <input
              id="end"
              type="date"
              className={`input ${errors.planned_end_date ? 'border-danger-500' : ''}`}
              value={draft.planned_end_date}
              onChange={(e) => {
                setDraft({ ...draft, planned_end_date: e.target.value });
                clearError('planned_end_date');
              }}
            />
            <FieldError field="planned_end_date" />
          </div>
          <div className={isBlinded ? '' : 'md:col-span-2'}>
            <label className="label text-xs" htmlFor="binder-template">
              {isBlinded ? 'Blinded binder template *' : 'Study binder template *'}
            </label>
            <select
              id="binder-template"
              className={`input ${errors.binder_template_id ? 'border-danger-500' : ''}`}
              value={draft.binder_template_id}
              onChange={(e) => {
                setDraft({ ...draft, binder_template_id: e.target.value });
                clearError('binder_template_id');
              }}
              disabled={bindersQ.isLoading}
            >
              <option value="">
                {bindersQ.isLoading ? 'Loading templates…' : '— Select template —'}
              </option>
              {blindedTemplates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.is_default === 'true' ? 'Default · ' : ''}
                  {t.name}
                  {t.readiness && !t.readiness.ready ? '  (not ready)' : ''}
                </option>
              ))}
            </select>
            <TemplateReadinessNote
              template={blindedTemplates.find((t) => t.template_id === draft.binder_template_id)}
            />
            <FieldError field="binder_template_id" />
          </div>
          {isBlinded && (
            <div>
              <label className="label text-xs" htmlFor="unblinded-binder-template">
                Unblinded binder template *
              </label>
              <select
                id="unblinded-binder-template"
                className={`input ${errors.unblinded_binder_template_id ? 'border-danger-500' : ''}`}
                value={draft.unblinded_binder_template_id}
                onChange={(e) => {
                  setDraft({ ...draft, unblinded_binder_template_id: e.target.value });
                  clearError('unblinded_binder_template_id');
                }}
                disabled={bindersQ.isLoading}
              >
                <option value="">
                  {bindersQ.isLoading ? 'Loading templates…' : '— Select template —'}
                </option>
                {unblindedTemplates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.is_default === 'true' ? 'Default · ' : ''}
                    {t.name}
                    {t.readiness && !t.readiness.ready ? '  (not ready)' : ''}
                  </option>
                ))}
              </select>
              <TemplateReadinessNote
                template={unblindedTemplates.find(
                  (t) => t.template_id === draft.unblinded_binder_template_id,
                )}
              />
              <FieldError field="unblinded_binder_template_id" />
            </div>
          )}
          <p className="md:col-span-2 text-xs text-ink-muted">
            {isBlinded
              ? 'Blinded studies get two binders: the blinded binder (Protocol, ICF, DoA, routing) for patient-facing staff and the unblinded binder (IP & randomisation uploads) for allocation-aware staff. '
              : "The folder layout this study's binder will be built from. "}
            Locks in when the study is activated. Add or edit templates under
            <b> Binder Templates</b>.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          {onCancel && (
            <button
              type="button"
              className="btn-ghost"
              onClick={onCancel}
              disabled={create.isPending}
            >
              Cancel
            </button>
          )}
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Create study & open wizard
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onboarding wizard (entry for /studies/:id/onboarding)
// ---------------------------------------------------------------------------

function OnboardingWizard({ studyId }: { studyId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // Tracks whether the user explicitly navigated (back/step-click). When true,
  // the auto-derive effect below won't override their chosen step.
  const manualNavRef = useRef(false);

  const ctxQ = useQuery({
    queryKey: ['onboarding', studyId],
    queryFn: () => onboarding.get(studyId),
  });

  // Hoisted so the Step-2 Next gate can check whether ≥1 protocol doc exists.
  // ProtocolUploadCard invalidates this same key on upload success, so the
  // button enables automatically without any extra wiring.
  const protocolQ = useQuery({
    queryKey: ['protocol-versions', studyId],
    queryFn: () => protocol.versions(studyId),
  });
  const hasProtocolDoc = (protocolQ.data?.versions ?? []).length > 0;

  // Active step = first non-completed step.
  const completedKeys = useMemo(() => {
    const set = new Set<string>();
    (ctxQ.data?.progress ?? []).forEach((p) => {
      if (p.step_status === 'completed') set.add(`step${p.step}`);
    });
    return set;
  }, [ctxQ.data]);

  const [activeKey, setActiveKey] = useState<string>(() => {
    // Default: first non-completed; if none, start at step 1.
    for (const def of STEP_DEFS) {
      if (!completedKeys.has(def.key)) return def.key;
    }
    return STEP_DEFS[0]!.key;
  });
  // Sync activeKey to the first uncompleted step after server progress updates.
  // Skipped when the user explicitly navigated (back/step-click) so that
  // staff-assignment refreshes don't kick them forward to the next step.
  useEffect(() => {
    if (!ctxQ.data || manualNavRef.current) return;
    for (const def of STEP_DEFS) {
      if (!completedKeys.has(def.key)) {
        setActiveKey(def.key);
        return;
      }
    }
    setActiveKey(STEP_DEFS[STEP_DEFS.length - 1]!.key);
  }, [completedKeys, ctxQ.data]);

  const stepDefsForStepper: StepDef[] = useMemo(
    () =>
      deriveStepStatuses(
        STEP_DEFS.map((d) => ({ key: d.key, label: d.label })),
        completedKeys,
        activeKey,
      ),
    [completedKeys, activeKey],
  );

  // Step-4 PI review checkboxes — all five must be ticked to enable Next.
  const [step4Checks, setStep4Checks] = useState({
    step1: false,
    step2: false,
    step3: false,
    step4: false,
    icf: false,
    consent: false,
  });
  const step4Confirmed = Object.values(step4Checks).every(Boolean);
  const step4ChecksInitRef = useRef(false);
  // Restore PI's saved checkbox state from the step-4 draft payload on first load.
  useEffect(() => {
    if (!ctxQ.data || step4ChecksInitRef.current) return;
    step4ChecksInitRef.current = true;
    const step4Progress = ctxQ.data.progress.find((p) => p.step === 4);
    if (step4Progress?.step_payload) {
      try {
        const saved = JSON.parse(step4Progress.step_payload) as Partial<typeof step4Checks>;
        setStep4Checks((prev) => ({ ...prev, ...saved }));
      } catch {
        /* ignore */
      }
    }
  }, [ctxQ.data]);

  // Step-2 draft kept in-memory; persisted via Save & Resume Later or Next.
  const [step2Draft, setStep2Draft] = useState<Step2Payload>({});
  useEffect(() => {
    if (!ctxQ.data) return;
    // visit_template comes from the step 2 payload (Protocol Setup).
    // All scalar fields (enrollment targets, phase, dates, etc.) are seeded
    // from the LIVE study/study_site rows — they are the authoritative source
    // and reflect changes from whichever step last updated them.
    let savedVisitTemplate: Step2Payload['visit_template'] = [];
    const step2Progress = ctxQ.data.progress.find((p) => p.step === 2);
    if (step2Progress) {
      try {
        const saved = JSON.parse(step2Progress.step_payload) as Step2Payload;
        savedVisitTemplate = saved.visit_template ?? [];
      } catch {
        /* ignore */
      }
    }
    setStep2Draft({
      protocol_number: ctxQ.data.study.protocol_number,
      title: ctxQ.data.study.title,
      sponsor: ctxQ.data.study.sponsor,
      sponsor_study_id: ctxQ.data.study.sponsor_study_id ?? '',
      phase: ctxQ.data.study.phase ?? '',
      therapeutic_area: ctxQ.data.study.therapeutic_area ?? '',
      planned_start_date: (ctxQ.data.study.planned_start_date ?? '').split('T')[0],
      planned_end_date: (ctxQ.data.study.planned_end_date ?? '').split('T')[0],
      enrollment_target: ctxQ.data.study.enrollment_target,
      enrollment_target_site:
        ctxQ.data.study_site.enrollment_target_site ?? ctxQ.data.study.enrollment_target,
      visit_template: savedVisitTemplate,
    });
  }, [ctxQ.data]);

  const completeMut = useMutation({
    mutationFn: ({ step, payload }: { step: OnboardingStepNumber; payload?: unknown }) =>
      onboarding.completeStep(studyId, step, payload),
    onSuccess: (_data, vars) => {
      toast.success(`${STEP_NAMES[vars.step]} completed`);
      manualNavRef.current = false; // allow auto-advance after explicit step completion
      qc.invalidateQueries({ queryKey: ['onboarding', studyId] });
    },
    onError: (err) =>
      toast.error(errorText(err, 'Failed to complete step')),
  });

  const saveDraftMut = useMutation({
    mutationFn: ({ step, payload }: { step: OnboardingStepNumber; payload: unknown }) =>
      onboarding.saveDraft(studyId, step, payload),
    onSuccess: () => {
      toast.success('Progress saved');
      qc.invalidateQueries({ queryKey: ['onboarding', studyId] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to save')),
  });

  const activateMut = useMutation({
    mutationFn: () => onboarding.activate(studyId),
    onSuccess: () => {
      toast.success('Study activated');
      qc.invalidateQueries({ queryKey: ['onboarding', studyId] });
      qc.invalidateQueries({ queryKey: ['portfolio-summary'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to activate')),
  });

  if (ctxQ.isLoading) {
    return (
      <div className="space-y-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-semibold text-ink-primary">Onboard study</h1>
            <p className="mt-1 text-sm text-ink-muted">Loading…</p>
          </div>
          <button type="button" className="btn-ghost text-xs" onClick={() => navigate('/studies')}>
            Exit
          </button>
        </header>
        <div className="card flex items-center justify-center gap-2 p-10 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading onboarding…
        </div>
      </div>
    );
  }
  if (ctxQ.isError || !ctxQ.data) {
    return (
      <div className={`card flex items-start gap-2 p-4 text-sm ${errorToneClass(ctxQ.error)}`}>
        {isPermissionError(ctxQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>
          {errorText(ctxQ.error, 'Failed to load onboarding context.')}
        </span>
      </div>
    );
  }

  const ctx = ctxQ.data;
  const activeStepNum = STEP_DEFS.find((s) => s.key === activeKey)?.step ?? 1;
  const isLastStep = activeKey === STEP_DEFS[STEP_DEFS.length - 1]!.key;

  function handleStepClick(key: string) {
    manualNavRef.current = true;
    setActiveKey(key);
  }

  function handleNext() {
    const stepNum = activeStepNum;
    if (stepNum === 1) {
      // Study fields are saved by the Study details panel on this step.
      completeMut.mutate({ step: 1, payload: {} });
    } else if (stepNum === 2) {
      completeMut.mutate({ step: 2, payload: step2Draft });
    } else if (stepNum === 4) {
      completeMut.mutate({ step: 4, payload: step4Checks });
    } else if (stepNum === 5) {
      completeMut.mutate({ step: 5 }, { onSuccess: () => activateMut.mutate() });
    } else {
      completeMut.mutate({ step: stepNum as OnboardingStepNumber });
    }
  }

  function handleSaveAndExit() {
    if (activeStepNum === 2) {
      saveDraftMut.mutate({ step: 2, payload: step2Draft });
    } else if (activeStepNum === 4) {
      saveDraftMut.mutate({ step: 4, payload: step4Checks });
    } else {
      saveDraftMut.mutate({ step: activeStepNum as OnboardingStepNumber, payload: {} });
    }
  }

  function handleBack() {
    manualNavRef.current = true;
    const idx = STEP_DEFS.findIndex((s) => s.key === activeKey);
    if (idx > 0) setActiveKey(STEP_DEFS[idx - 1]!.key);
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold text-ink-primary">
            Onboard study: {ctx.study.title}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            <span className="font-mono">{ctx.study.protocol_number}</span> ·{' '}
            <strong>Sponsor:</strong> {ctx.study.sponsor}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={studyStatusBadgeClass(ctx.study_site.status)}>
            {humanStatus(ctx.study_site.status)}
          </span>
          <button type="button" className="btn-ghost text-xs" onClick={() => navigate('/studies')}>
            Exit
          </button>
        </div>
      </header>

      <WizardShell
        steps={stepDefsForStepper}
        activeKey={activeKey}
        onStepClick={handleStepClick}
        auditPanel={
          <AuditTrailPanel scope={{ studyId, studySiteId: ctx.study_site.study_site_id }} />
        }
        onBack={handleBack}
        onNext={handleNext}
        onSaveAndExit={handleSaveAndExit}
        isAdvancing={completeMut.isPending || activateMut.isPending}
        isSaving={saveDraftMut.isPending}
        nextLabel={isLastStep ? 'Activate' : 'Next'}
        nextDisabled={
          (activeKey === 'step2' && !hasProtocolDoc) ||
          (activeKey === 'step3' &&
            (!ctx.readiness.staff?.value || !ctx.readiness.econsent?.value)) ||
          (activeKey === 'step4' && !step4Confirmed) ||
          (activeKey === 'step5' &&
            (!(
              ctx.readiness.staff?.value &&
              ctx.readiness.econsent?.value &&
              ctx.readiness.ebinder?.value &&
              ctx.readiness.esource?.value &&
              ctx.progress.some((p) => p.step === 4 && p.step_status === 'completed')
            ) ||
              ctx.study_site.status === 'active'))
        }
      >
        {activeKey === 'step1' && <Step1SiteInvitation ctx={ctx} />}
        {activeKey === 'step2' && (
          <Step2ProtocolSetup studyId={studyId} draft={step2Draft} setDraft={setStep2Draft} />
        )}
        {activeKey === 'step3' && (
          <Step3SiteCustomisation
            studyId={studyId}
            ctx={ctx}
            onRefresh={() => qc.invalidateQueries({ queryKey: ['onboarding', studyId] })}
            refreshing={ctxQ.isFetching}
            onGoToStep={(key) => setActiveKey(key)}
          />
        )}
        {activeKey === 'step4' && (
          <Step4eSignatureApproval
            studyId={studyId}
            ctx={ctx}
            checks={step4Checks}
            onCheck={(key, val) => setStep4Checks((prev) => ({ ...prev, [key]: val }))}
          />
        )}
        {activeKey === 'step5' && <Step5Activation ctx={ctx} />}
      </WizardShell>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Site Invitation
// ---------------------------------------------------------------------------

function Step1SiteInvitation({ ctx }: { ctx: OnboardingContext }) {
  const { user } = useAuth();
  const siteName = user?.siteNames?.[ctx.study_site.site_id] ?? ctx.study_site.site_id;
  const studyLabel = ctx.study.title
    ? `${ctx.study.title}${ctx.study.protocol_number ? ` — ${ctx.study.protocol_number}` : ''}`
    : ctx.study.protocol_number || ctx.study.study_id;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold mb-1">Study Invitation</h2>
        <p className="text-sm text-ink-muted">
          Confirm the study details below. This site is already registered for this study.
        </p>
      </div>

      <div className="rounded-md border border-surface-border bg-surface-muted/40 p-3 text-sm">
        <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">
          Active study-site
        </div>
        <div>
          <strong>Study:</strong> {studyLabel}
        </div>
        <div>
          <strong>Site:</strong> {siteName}
        </div>
        <div>
          <strong>Status:</strong> {humanStatus(ctx.study_site.status)}
        </div>
      </div>

      {/* Harish, 2 Oct 2026: every field is editable here except protocol
          number, title and sponsor; blinding design and binder templates
          until the study's binder exists. Saved straight to the study. */}
      <StudyDetailsPanel studyId={ctx.study.study_id} study={ctx.study} studySite={ctx.study_site} editable />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Protocol Setup (includes visit-schedule template editor)
// ---------------------------------------------------------------------------

const VISIT_CATEGORY_OPTIONS: { value: VisitCategory; label: string }[] = [
  { value: 'screening', label: 'Screening' },
  { value: 'pre_screening', label: 'Pre-screening' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'unscheduled', label: 'Unscheduled' },
  { value: 'ad_hoc', label: 'Ad hoc' },
  { value: 'follow_up', label: 'Follow-up' },
];

function Step2ProtocolSetup({
  studyId,
  draft,
  setDraft,
}: {
  studyId: string;
  draft: Step2Payload;
  setDraft: (d: Step2Payload) => void;
}) {
  const visits = draft.visit_template ?? [];

  function addVisit() {
    setDraft({
      ...draft,
      visit_template: [
        ...visits,
        {
          visit_name: `Visit ${visits.length + 1}`,
          visit_order: visits.length + 1,
          nominal_day: visits.length === 0 ? 0 : visits[visits.length - 1]!.nominal_day + 7,
          window_minus_days: 3,
          window_plus_days: 3,
          required_visit_type: 'scheduled',
          visit_category: 'scheduled',
          is_billable: false,
          billable_amount_cents: null,
          fixed_fee_cents: null,
        },
      ],
    });
  }

  function patchVisit(idx: number, patch: Partial<VisitTemplateEntry>) {
    setDraft({
      ...draft,
      visit_template: visits.map((v, i) => (i === idx ? { ...v, ...patch } : v)),
    });
  }

  /**
   * Client feedback 2026-08-04 — billability is contractual, not tied to
   * category: "even though there is a condition, we can remove that
   * option... let the who are is building the study take a call." Changing
   * category no longer touches an already-set billable flag/amount.
   */
  function patchCategory(idx: number, category: VisitCategory) {
    patchVisit(idx, { visit_category: category });
  }

  function removeVisit(idx: number) {
    setDraft({ ...draft, visit_template: visits.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold mb-1">Protocol Setup</h2>
        <p className="text-sm text-ink-muted">
          Upload the protocol document and define the visit-schedule template for this study.
        </p>
      </div>

      <ProtocolUploadCard studyId={studyId} />

      <div className="max-w-sm">
        <label className="label text-xs" htmlFor="sponsor_study_id_step2">
          Sponsor Study ID (optional)
        </label>
        <input
          id="sponsor_study_id_step2"
          maxLength={120}
          className="input"
          value={draft.sponsor_study_id ?? ''}
          onChange={(e) => setDraft({ ...draft, sponsor_study_id: e.target.value })}
          placeholder="Sponsor's own identifier for this study"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-ink-primary">Visit schedule template</h3>
          <button type="button" className="btn-secondary text-xs" onClick={addVisit}>
            <Plus className="h-3.5 w-3.5" /> Add visit
          </button>
        </div>
        {visits.length === 0 && (
          <p className="text-xs text-ink-muted">
            No visits yet. Add at least one to define the schedule of assessments.
          </p>
        )}
        {visits.length > 0 && (
          <table className="min-w-full text-sm border border-surface-border">
            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-2 py-1.5">#</th>
                <th className="px-2 py-1.5">Visit name</th>
                <th className="px-2 py-1.5">Type</th>
                <th className="px-2 py-1.5">Day</th>
                <th className="px-2 py-1.5">- window</th>
                <th className="px-2 py-1.5">+ window</th>
                <th className="px-2 py-1.5">Billable</th>
                <th className="px-2 py-1.5">Amount (per subject)</th>
                <th className="px-2 py-1.5">Fixed fee (per visit)</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {visits.map((v, idx) => {
                const category = v.visit_category ?? 'scheduled';
                const billable = !!v.is_billable;
                return (
                  <tr key={idx} className="border-t border-surface-border">
                    <td className="px-2 py-1 text-xs text-ink-muted">{v.visit_order}</td>
                    <td className="px-2 py-1">
                      <input
                        className="input py-1 text-xs"
                        value={v.visit_name}
                        onChange={(e) => patchVisit(idx, { visit_name: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <select
                        className="input py-1 text-xs"
                        value={category}
                        onChange={(e) => patchCategory(idx, e.target.value as VisitCategory)}
                      >
                        {VISIT_CATEGORY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <input
                        className="input py-1 text-xs w-20"
                        type="number"
                        value={v.nominal_day}
                        onChange={(e) =>
                          patchVisit(idx, { nominal_day: parseInt(e.target.value || '0', 10) })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        className="input py-1 text-xs w-16"
                        type="number"
                        min={0}
                        value={v.window_minus_days}
                        onChange={(e) =>
                          patchVisit(idx, { window_minus_days: parseInt(e.target.value || '0', 10) })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        className="input py-1 text-xs w-16"
                        type="number"
                        min={0}
                        value={v.window_plus_days}
                        onChange={(e) =>
                          patchVisit(idx, { window_plus_days: parseInt(e.target.value || '0', 10) })
                        }
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={billable}
                        onChange={(e) => {
                          const nextBillable = e.target.checked;
                          patchVisit(idx, {
                            is_billable: nextBillable,
                            billable_amount_cents: nextBillable ? v.billable_amount_cents : null,
                            fixed_fee_cents: nextBillable ? v.fixed_fee_cents : null,
                          });
                        }}
                      />
                    </td>
                    <td className="px-2 py-1">
                      {billable ? (
                        <input
                          className="input py-1 text-xs w-24"
                          inputMode="decimal"
                          placeholder="0.00"
                          defaultValue={fmtCentsInput(v.billable_amount_cents)}
                          onBlur={(e) => {
                            const cents = parseMoneyToCents(e.target.value);
                            patchVisit(idx, { billable_amount_cents: cents ?? 0 });
                          }}
                        />
                      ) : (
                        <span className="text-xs text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {billable ? (
                        <input
                          className="input py-1 text-xs w-24"
                          inputMode="decimal"
                          placeholder="0.00"
                          title="Flat fee paid to the site once per invoice, regardless of subject count"
                          defaultValue={fmtCentsInput(v.fixed_fee_cents)}
                          onBlur={(e) => {
                            const cents = parseMoneyToCents(e.target.value);
                            patchVisit(idx, { fixed_fee_cents: cents ?? 0 });
                          }}
                        />
                      ) : (
                        <span className="text-xs text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        className="text-ink-muted hover:text-danger-700"
                        onClick={() => removeVisit(idx)}
                        aria-label="Remove visit"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {visits.some((v) => v.is_billable) && (
          <p className="mt-2 text-xs text-ink-muted">
            Billable visits: {visits.filter((v) => v.is_billable).length} · Schedule value per subject:{' '}
            {fmtMoney(visits.reduce((sum, v) => sum + (v.is_billable ? (v.billable_amount_cents ?? 0) : 0), 0))}
          </p>
        )}
      </div>

      {/* Study configuration, moved into the flow that sets it up.
          Both panels existed but were mounted only on the workspace hub, so
          they were reachable only AFTER the study was built — which is the
          wrong moment to decide which DoA responsibilities it offers or how
          it grades an adverse event. They save on their own (each has its own
          Save button and endpoint); they are not part of the step's draft
          payload, so nothing here needs to round-trip them. */}
      <div className="space-y-4 border-t border-surface-border pt-6">
        <div>
          <h3 className="text-sm font-semibold text-ink-primary">Study configuration</h3>
          <p className="text-xs text-ink-muted">
            Set these now so they are in place before the study takes subjects. Each saves on its own.
          </p>
        </div>
        <DoaResponsibilitiesPanel studyId={studyId} />
        <AdverseEventConfigPanel studyId={studyId} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Study Customisation (dynamic, derived-from-data readiness)
// ---------------------------------------------------------------------------

/**
 * Per-flag guidance shown in the amber "Not ready" callout. Tells the user
 * the concrete next step they need to take elsewhere in the app so the
 * derived flag flips to Ready on the next BE re-check.
 */
const READINESS_GUIDANCE: Record<ReadinessFlagKey, string> = {
  econsent: 'Add an active consent document for this study',
  ebinder: 'Upload a protocol document in Step 2',
  staff: 'Principal Investigator is mandatory for activation of the study',
  esource: 'Define at least one visit in the schedule template (Step 2)',
};

const READINESS_FLAG_ORDER: ReadinessFlagKey[] = ['staff', 'ebinder', 'econsent', 'esource'];

/**
 * The static guidance above assumes the blocker is "nothing uploaded yet".
 * That's often wrong for eConsent, which has two other real blockers whose
 * fix is NOT "add a document" — the ICF exists and is waiting on an
 * approver (Feedback #4 approval flow), or the ICF exists and is approved
 * but has no signature layout placed yet (bug found live 24 Sep 2026: this
 * previously wasn't checked at all here, so a study could activate and only
 * discover the missing layout later, mid-subject-onboarding). deriveEconsent
 * (BE) already words both cases precisely, so prefer its reason over the
 * generic static string whenever it applies.
 */
function guidanceFor(k: ReadinessFlagKey, flag: DerivedFlag | undefined): string {
  if (k === 'econsent' && (flag?.reason?.includes('awaiting approval') || flag?.reason?.includes('no signature layout'))) {
    return flag.reason;
  }
  return READINESS_GUIDANCE[k];
}

function Step3SiteCustomisation({
  ctx,
  studyId,
  onRefresh,
  refreshing,
  onGoToStep,
}: {
  ctx: OnboardingContext;
  studyId: string;
  onRefresh: () => void;
  refreshing: boolean;
  /** Hop to another wizard step (used by the "Back to Step 2" callout). */
  onGoToStep: (stepKey: string) => void;
}) {
  const navigate = useNavigate();
  const readiness = ctx.readiness;
  const allReady = READINESS_FLAG_ORDER.every((k) => readiness[k]?.value === true);

  /**
   * Per-flag "Fix the gap" action — surfaced as a small btn-secondary link
   * inside each amber callout. ebinder has no dedicated destination (the
   * protocol upload card lives in step 2 alongside visits), so we route it
   * back to step 2 too.
   */
  function renderCalloutAction(k: ReadinessFlagKey) {
    if (k === 'econsent') {
      return (
        <button
          type="button"
          className="btn-secondary text-xs whitespace-nowrap"
          onClick={() => navigate(`/studies/${studyId}#consent-documents`)}
        >
          Open consent documents
        </button>
      );
    }
    if (k === 'staff') {
      return null;
    }
    if (k === 'esource' || k === 'ebinder') {
      return (
        <button
          type="button"
          className="btn-secondary text-xs whitespace-nowrap"
          onClick={() => onGoToStep('step2')}
        >
          Back to Step 2
        </button>
      );
    }
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Study Customisation</h2>
        <p className="text-sm text-ink-muted">
          Readiness for the four activation components is derived automatically from your study data
          — uploads, team assignments, consent documents, and visit-template configuration. There
          are no manual toggles; complete the underlying work and the status updates here.
        </p>
      </div>

      <div className="space-y-2">
        {READINESS_FLAG_ORDER.map((key) => {
          if (key === 'staff') {
            return (
              <StaffCard
                key="staff"
                studyId={studyId}
                flag={readiness.staff}
                blindingType={ctx.study.blinding_type ?? null}
                onRefresh={onRefresh}
              />
            );
          }
          if (key === 'econsent') {
            return (
              <EConsentCard
                key="econsent"
                studyId={studyId}
                flag={readiness.econsent}
                onRefresh={onRefresh}
              />
            );
          }
          return <ReadinessFlagCard key={key} flagKey={key} flag={readiness[key]} />;
        })}
      </div>

      {allReady ? (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>All systems Ready</strong> — you can proceed to Next Step.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {READINESS_FLAG_ORDER.filter((k) => !readiness[k]?.value).map((k) => (
            <div
              key={k}
              className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <strong>{humanReadiness(k)} not ready</strong> — {guidanceFor(k, readiness[k])}.
              </div>
              {renderCalloutAction(k)}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh status
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff card — inline user + role assignment
// ---------------------------------------------------------------------------

function StaffCard({
  studyId,
  flag,
  blindingType,
  onRefresh,
}: {
  studyId: string;
  flag: DerivedFlag | undefined;
  /** Blinded studies require a Blinded / Unblinded designation on every new assignment. */
  blindingType?: StudyBlindingType | null;
  onRefresh: () => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState<'blinded' | 'unblinded' | ''>('');
  const needsDesignation = isBlindedStudyType(blindingType ?? null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState('');
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const staffQ = useQuery({
    queryKey: ['study-staff', studyId],
    queryFn: () => studyStaff.list(studyId),
  });

  const assignMut = useMutation({
    mutationFn: (vars: { user_id: string; role: string; blinded?: boolean }) =>
      studyStaff.assign(studyId, vars),
    onSuccess: () => {
      const isEdit = editingId !== null;
      toast.success(isEdit ? 'Role updated' : 'Staff member assigned');
      void staffQ.refetch();
      onRefresh();
      setSelectedUserId('');
      setSelectedRole('');
      setSelectedDesignation('');
      setAddOpen(false);
      setEditingId(null);
      setEditingRole('');
    },
    onError: (err) => toast.error(errorText(err, 'Failed to assign')),
  });

  const revokeMut = useMutation({
    mutationFn: (delegationId: string) => studyStaff.revoke(studyId, delegationId),
    onSuccess: () => {
      toast.success('Staff member removed');
      setRevokingId(null);
      void staffQ.refetch();
      onRefresh();
    },
    onError: (err) => {
      setRevokingId(null);
      toast.error(errorText(err, 'Failed to remove'));
    },
  });

  const { assignments = [], candidates = [] } = staffQ.data ?? {};

  const hasPi = assignments.some((a) => a.role === 'principal_investigator');
  const piDelegationId = assignments.find(
    (a) => a.role === 'principal_investigator',
  )?.delegation_id;

  // Roles available in the add form — PI is locked once one is assigned
  const addableRoles = ASSIGNABLE_STAFF_ROLES.filter(
    (r) => r !== 'principal_investigator' || !hasPi,
  );

  // Roles available when editing — PI blocked if another assignment already holds it
  function editableRoles(delegationId: string) {
    return ASSIGNABLE_STAFF_ROLES.filter(
      (r) => r !== 'principal_investigator' || !hasPi || delegationId === piDelegationId,
    );
  }

  return (
    <div className="rounded-md border border-surface-border bg-white p-3">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-4 w-4 text-ink-muted shrink-0" />
        <span className="font-medium text-ink-primary">Staff</span>
        {flag?.value ? (
          <span className="badge badge-success text-[10px]">Ready</span>
        ) : (
          <span className="badge badge-warn text-[10px]">Not ready</span>
        )}
        {staffQ.isFetching && (
          <span className="ml-auto h-2 w-2 rounded-full bg-surface-muted animate-pulse" />
        )}
      </div>
      <p className="text-xs text-ink-muted mb-3">
        {flag?.reason ??
          'A Principal Investigator must be assigned before the study can be activated.'}
      </p>
      {!flag?.value && assignments.length > 0 && !hasPi && (
        <div className="mb-3 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />A{' '}
          <strong className="mx-0.5">Principal Investigator</strong> is required. Assign one before
          activating the study.
        </div>
      )}

      {/* Skeleton while loading */}
      {staffQ.isLoading && (
        <div className="mb-3 space-y-1.5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded border border-surface-border bg-surface-muted/30 px-2.5 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-3.5 w-28 rounded bg-surface-muted" />
                  <div className="h-3 w-16 rounded bg-surface-muted/70" />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <div className="h-3.5 w-3.5 rounded bg-surface-muted" />
                  <div className="h-3.5 w-3.5 rounded bg-surface-muted" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Current assignments */}
      {!staffQ.isLoading && assignments.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {assignments.map((a) => {
            const isEditing = editingId === a.delegation_id;
            return (
              <div
                key={a.delegation_id}
                className="rounded border border-surface-border bg-surface-muted/30 px-2.5 py-2"
              >
                {isEditing ? (
                  /* ── Inline edit row ── */
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm text-ink-primary min-w-0 truncate flex-1">
                      {a.full_name || a.email}
                    </span>
                    <select
                      className="input text-xs py-1 flex-1 min-w-[150px]"
                      value={editingRole}
                      onChange={(e) => setEditingRole(e.target.value)}
                      autoFocus
                    >
                      {editableRoles(a.delegation_id).map((r) => (
                        <option key={r} value={r}>
                          {humanRole(r)}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        className="btn-primary text-xs py-1 px-2.5 flex items-center gap-1"
                        disabled={!editingRole || editingRole === a.role || assignMut.isPending}
                        onClick={() => assignMut.mutate({ user_id: a.user_id, role: editingRole })}
                      >
                        {assignMut.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Check className="h-3 w-3" />
                        )}
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn-ghost text-xs py-1 px-2"
                        onClick={() => {
                          setEditingId(null);
                          setEditingRole('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── View row ── */
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-sm text-ink-primary truncate">
                        {a.full_name || a.email}
                      </span>
                      <span className="text-xs text-ink-muted shrink-0">{humanRole(a.role)}</span>
                      {a.active === false && <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium text-ink-muted shrink-0" title="Deactivated or removed from the site">Inactive</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(a.delegation_id);
                          setEditingRole(a.role);
                          setAddOpen(false);
                        }}
                        disabled={assignMut.isPending || revokeMut.isPending}
                        className="text-ink-muted hover:text-navy transition-colors p-0.5"
                        aria-label={`Edit role for ${a.full_name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRevokingId(a.delegation_id);
                          revokeMut.mutate(a.delegation_id);
                        }}
                        disabled={revokingId === a.delegation_id || assignMut.isPending}
                        className="text-ink-muted hover:text-danger-700 transition-colors p-0.5 disabled:cursor-not-allowed"
                        aria-label={`Remove ${a.full_name}`}
                      >
                        {revokingId === a.delegation_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <X className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      {staffQ.isLoading ? null : !addOpen ? (
        <button
          type="button"
          onClick={() => {
            setAddOpen(true);
            setEditingId(null);
          }}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-navy hover:text-navy-600 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add staff member
        </button>
      ) : (
        <>
        <div className="flex flex-wrap gap-2 items-end pt-1 border-t border-surface-border mt-1">
          <div className="flex-1 min-w-[160px]">
            <label className="label text-[10px]">Team member</label>
            <select
              className="input text-sm py-1.5"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">Select member…</option>
              {candidates.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.full_name || c.email}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="label text-[10px]">Role on this study</label>
            <select
              className="input text-sm py-1.5"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="">Select role…</option>
              {addableRoles.map((r) => (
                <option key={r} value={r}>
                  {humanRole(r)}
                </option>
              ))}
            </select>
          </div>
          {needsDesignation && (
            <fieldset className="w-full" data-testid="wizard-staff-blinding">
              <legend className="label text-[10px]">
                Blinding designation <span className="text-danger-600">*</span>
              </legend>
              <div className="flex flex-wrap gap-3 text-xs">
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="wizard-staff-designation"
                    checked={selectedDesignation === 'blinded'}
                    onChange={() => setSelectedDesignation('blinded')}
                  />
                  <b>Blinded</b>
                  <span className="text-ink-muted">— patient-facing, no IP documents</span>
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="wizard-staff-designation"
                    checked={selectedDesignation === 'unblinded'}
                    onChange={() => setSelectedDesignation('unblinded')}
                  />
                  <b>Unblinded</b>
                  <span className="text-ink-muted">— allocation-aware, no subject identity</span>
                </label>
              </div>
            </fieldset>
          )}
          <div className="flex gap-1.5 pb-0.5">
            <button
              type="button"
              className="btn-primary text-xs py-1.5 px-3"
              disabled={
                !selectedUserId || !selectedRole || (needsDesignation && !selectedDesignation) || assignMut.isPending
              }
              onClick={() =>
                assignMut.mutate({
                  user_id: selectedUserId,
                  role: selectedRole,
                  ...(needsDesignation ? { blinded: selectedDesignation === 'blinded' } : {}),
                })
              }
            >
              {assignMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Assign'}
            </button>
            <button
              type="button"
              className="btn-ghost text-xs py-1.5 px-2"
              onClick={() => {
                setAddOpen(false);
                setSelectedUserId('');
                setSelectedRole('');
                setSelectedDesignation('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
        {candidates.length === 0 && !staffQ.isFetching && (
          <p className="mt-1 text-[10px] text-ink-muted">
            All site members already assigned.
          </p>
        )}
        </>
      )}
    </div>
  );
}

function EConsentCard({
  studyId,
  flag,
  onRefresh,
}: {
  studyId: string;
  flag: DerivedFlag | undefined;
  onRefresh: () => void;
}) {
  if (!flag) {
    return (
      <div className="rounded-md border border-surface-border bg-white p-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink-primary">{humanReadiness('econsent')}</span>
          <span className="badge badge-neutral text-[10px]">Unknown</span>
        </div>
        <p className="mt-1 text-xs text-ink-muted">No readiness data returned by server.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-surface-border bg-white p-3">
      <div className="flex items-center gap-2">
        <span className="font-medium text-ink-primary">{humanReadiness('econsent')}</span>
        {flag.value ? (
          <span className="badge badge-success text-[10px]">Ready</span>
        ) : (
          <span className="badge badge-warn text-[10px]">Not ready</span>
        )}
      </div>
      <p className="mt-1 text-xs text-ink-muted">{flag.reason}</p>
      <ConsentUploadCard studyId={studyId} onRefresh={onRefresh} />
    </div>
  );
}

function ReadinessFlagCard({
  flagKey,
  flag,
}: {
  flagKey: ReadinessFlagKey;
  flag: DerivedFlag | undefined;
}) {
  const [open, setOpen] = useState(false);

  // Defensive: if the BE response is missing a flag for any reason, show a
  // neutral "unknown" tile rather than crashing on the optional chain.
  if (!flag) {
    return (
      <div className="rounded-md border border-surface-border bg-white p-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink-primary">{humanReadiness(flagKey)}</span>
          <span className="badge badge-neutral text-[10px]">Unknown</span>
        </div>
        <p className="mt-1 text-xs text-ink-muted">No readiness data returned by server.</p>
      </div>
    );
  }

  const hasEvidence =
    flag.evidence && typeof flag.evidence === 'object' && Object.keys(flag.evidence).length > 0;

  return (
    <div className="rounded-md border border-surface-border bg-white p-3">
      <div className="flex items-center gap-2">
        <span className="font-medium text-ink-primary">{humanReadiness(flagKey)}</span>
        {flag.value ? (
          <span className="badge badge-success text-[10px]">Ready</span>
        ) : (
          <span className="badge badge-warn text-[10px]">Not ready</span>
        )}
      </div>
      <p className="mt-1 text-xs text-ink-muted">{flag.reason}</p>
      {hasEvidence && (
        <div className="mt-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink-primary"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            View evidence
          </button>
          {open && (
            <div className="mt-1.5">
              <EvidenceDisplay flagKey={flagKey} evidence={flag.evidence} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EvidenceDisplay({
  flagKey,
  evidence,
}: {
  flagKey: ReadinessFlagKey;
  evidence: Record<string, unknown>;
}) {
  if (flagKey === 'ebinder') {
    const docs = evidence.documents as
      | Array<{
          document_id: string;
          file_name: string;
          version_label: string;
          file_size: number;
        }>
      | undefined;

    if (docs && docs.length > 0) {
      return (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div
              key={d.document_id}
              className="flex items-center gap-2.5 rounded border border-surface-border bg-surface-muted/40 px-2.5 py-2"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-navy-50 text-navy">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-ink-primary">{d.file_name}</div>
                <div className="text-[10px] text-ink-muted">
                  Version {d.version_label}
                  {d.file_size ? ` · ${(d.file_size / 1024).toFixed(0)} KB` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
  }

  if (flagKey === 'econsent') {
    const versionLabel = evidence.version_label as string | undefined;
    const irbName = evidence.irb_name as string | undefined;
    const irbDate = evidence.irb_approval_date as string | undefined;
    if (versionLabel) {
      return (
        <div className="rounded border border-surface-border bg-surface-muted/40 px-2.5 py-2 text-xs space-y-0.5">
          <div className="font-medium text-ink-primary">ICF {versionLabel}</div>
          {irbName && <div className="text-ink-muted">IRB: {irbName}</div>}
          {irbDate && <div className="text-ink-muted">Approval date: {irbDate.slice(0, 10)}</div>}
        </div>
      );
    }
  }

  // Generic fallback: readable key-value list (no raw braces)
  const entries = Object.entries(evidence).filter(
    ([, v]) => v !== null && v !== undefined && !Array.isArray(v) && typeof v !== 'object',
  );
  if (entries.length === 0) return null;
  return (
    <dl className="rounded border border-surface-border bg-surface-muted/40 px-2.5 py-2 text-[11px] space-y-0.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-1.5">
          <dt className="text-ink-muted capitalize">{k.replace(/_/g, ' ')}:</dt>
          <dd className="text-ink-primary font-medium">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — eSignature Approval
// ---------------------------------------------------------------------------

type Step4Checks = {
  step1: boolean;
  step2: boolean;
  step3: boolean;
  step4: boolean;
  icf: boolean;
  consent: boolean;
};

function Step4eSignatureApproval({
  studyId,
  ctx,
  checks,
  onCheck,
}: {
  studyId: string;
  ctx: OnboardingContext;
  checks: Step4Checks;
  onCheck: (key: keyof Step4Checks, val: boolean) => void;
}) {
  const { user } = useAuth();

  const staffQ = useQuery({
    queryKey: ['study-staff', studyId],
    queryFn: () => studyStaff.list(studyId),
  });

  const protocolQ = useQuery({
    queryKey: ['protocol-versions', studyId],
    queryFn: () => protocol.versions(studyId),
  });

  const consentQ = useQuery({
    queryKey: ['consent', 'documents', studyId],
    queryFn: () => consent.listDocuments(studyId),
  });
  const activeConsentDoc = consentQ.data?.documents.find((d) => d.is_active) ?? null;

  const [viewDocLoading, setViewDocLoading] = useState<string | null>(null);
  const [viewConsentDocLoading, setViewConsentDocLoading] = useState(false);

  async function openDoc(docId: string, _fileName: string) {
    setViewDocLoading(docId);
    try {
      const r = await protocol.download(studyId, docId);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } finally {
      setViewDocLoading(null);
    }
  }

  async function openConsentDoc(docId: string) {
    setViewConsentDocLoading(true);
    try {
      const r = await consent.documentDownloadUrl(docId);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } finally {
      setViewConsentDocLoading(false);
    }
  }

  const step2Payload = useMemo<Step2Payload>(() => {
    const row = ctx.progress.find((p) => p.step === 2);
    if (!row?.step_payload) return {};
    try {
      return JSON.parse(row.step_payload) as Step2Payload;
    } catch {
      return {};
    }
  }, [ctx.progress]);

  const assignments = staffQ.data?.assignments ?? [];
  const protocolDocs = protocolQ.data?.versions ?? [];

  // Check study-level role, not site role — a site admin may be the PI for this study.
  const isStudyPI = assignments.some(
    (a) => a.user_id === user?.id && a.role === 'principal_investigator',
  );

  if (staffQ.isLoading) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">eSignature Approval</h2>
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (!isStudyPI) {
    const step4Done = ctx.progress.some((p) => p.step === 4 && p.step_status === 'completed');
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">eSignature Approval</h2>
        {step4Done ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>Principal Investigator has reviewed and approved this study.</span>
          </div>
        ) : (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 flex items-center gap-2">
            <Info className="h-4 w-4 shrink-0" />
            <span>Principal Investigator yet to be reviewed and approved.</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">eSignature Approval</h2>
      <p className="text-sm text-ink-muted">
        Review each step below and tick the confirmation checkbox. All four must be checked before
        you can proceed.
      </p>

      <ReviewSection
        label="1. Study Invitation"
        checked={checks.step1}
        onCheck={(v) => onCheck('step1', v)}
        confirmText="I confirm the study invitation details are correct"
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Protocol</dt>
            <dd className="font-medium">{ctx.study.protocol_number}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Sponsor</dt>
            <dd className="font-medium">{ctx.study.sponsor}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Title</dt>
            <dd className="font-medium">{ctx.study.title}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Therapeutic Area</dt>
            <dd className="font-medium">{ctx.study.therapeutic_area ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Status</dt>
            <dd className="font-medium">{humanStatus(ctx.study_site.status)}</dd>
          </div>
          <BlindingSummary study={ctx.study} />
        </dl>
      </ReviewSection>

      <ReviewSection
        label="2. Protocol Setup"
        checked={checks.step2}
        onCheck={(v) => onCheck('step2', v)}
        confirmText="I confirm the protocol setup details are correct"
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Phase</dt>
            <dd className="font-medium">{step2Payload.phase ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">
              Global Enrollment Target
            </dt>
            <dd className="font-medium">{step2Payload.enrollment_target ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Planned Start</dt>
            <dd className="font-medium">{step2Payload.planned_start_date ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Planned End</dt>
            <dd className="font-medium">{step2Payload.planned_end_date ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">
              Site Enrollment Cap
            </dt>
            <dd className="font-medium">{step2Payload.enrollment_target_site ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Visit Templates</dt>
            <dd className="font-medium">{step2Payload.visit_template?.length ?? 0} visits</dd>
          </div>
        </dl>
      </ReviewSection>

      <ReviewSection
        label="3. Study Customisation"
        checked={checks.step3}
        onCheck={(v) => onCheck('step3', v)}
        confirmText="I confirm the study customisation details are correct"
      >
        <div className="space-y-3">
          {staffQ.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading staff…
            </div>
          ) : assignments.length === 0 ? (
            <p className="text-sm text-ink-muted">No staff assigned.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-surface-border text-ink-muted">
                  <th className="pb-1.5 text-left font-normal">Name</th>
                  <th className="pb-1.5 text-left font-normal">Role</th>
                  <th className="pb-1.5 text-left font-normal">Email</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr
                    key={a.delegation_id}
                    className="border-b border-surface-border/50 last:border-0"
                  >
                    <td className="py-1.5 pr-3 font-medium">{a.full_name}</td>
                    <td className="py-1.5 pr-3">{humanRole(a.role)}</td>
                    <td className="py-1.5 text-ink-muted">{a.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="grid grid-cols-2 gap-2">
            {(['staff', 'ebinder', 'econsent', 'esource'] as ReadinessFlagKey[]).map((k) => {
              const flag = ctx.readiness[k];
              return (
                <div
                  key={k}
                  className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs ${flag?.value ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
                >
                  {flag?.value ? (
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                  ) : (
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                  )}
                  {humanReadiness(k)}
                </div>
              );
            })}
          </div>
        </div>
      </ReviewSection>

      {/* Protocol Documents */}
      <ReviewSection
        label="4. Protocol Documents"
        checked={checks.step4}
        onCheck={(v) => onCheck('step4', v)}
        confirmText="I confirm I have reviewed all protocol documents"
      >
        {protocolQ.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading documents…
          </div>
        ) : protocolDocs.length === 0 ? (
          <p className="text-sm text-ink-muted">No protocol documents uploaded.</p>
        ) : (
          <ul className="space-y-1.5">
            {protocolDocs.map((doc) => (
              <li
                key={doc.document_id}
                className="flex items-center justify-between gap-3 rounded-md border border-surface-border bg-white px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                  <button
                    type="button"
                    onClick={() => openDoc(doc.document_id, doc.file_name)}
                    disabled={viewDocLoading === doc.document_id}
                    className="text-sm font-medium truncate text-navy hover:underline text-left"
                  >
                    {doc.file_name}
                  </button>
                  <span className="text-[11px] text-ink-muted shrink-0">{doc.version_label}</span>
                </div>
                <button
                  type="button"
                  onClick={() => openDoc(doc.document_id, doc.file_name)}
                  disabled={viewDocLoading === doc.document_id}
                  className="shrink-0 text-xs font-medium text-navy hover:underline transition-colors"
                  aria-label={`View ${doc.file_name}`}
                >
                  {viewDocLoading === doc.document_id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    'View'
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </ReviewSection>

      {/* ICF Review */}
      <ReviewSection
        label="5. Informed Consent Form (ICF)"
        checked={checks.icf}
        onCheck={(v) => onCheck('icf', v)}
        confirmText="I confirm the informed consent document version is correct and approved for subject signing"
      >
        {consentQ.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading consent documents…
          </div>
        ) : !activeConsentDoc ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>No active informed consent document — go back to Step 3 to upload one.</span>
          </div>
        ) : (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div>
                <dt className="text-[11px] text-ink-muted uppercase tracking-wide">ICF Version</dt>
                <dd className="font-medium">{activeConsentDoc.version_label}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-ink-muted uppercase tracking-wide">Version Date</dt>
                <dd className="font-medium">
                  {activeConsentDoc.version_date?.slice(0, 10) ?? '—'}
                </dd>
              </div>
            </dl>
            <ul className="space-y-1.5">
              <li className="flex items-center justify-between gap-3 rounded-md border border-surface-border bg-white px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                  <button
                    type="button"
                    onClick={() => openConsentDoc(activeConsentDoc.consent_document_id)}
                    disabled={viewConsentDocLoading}
                    className="truncate text-left text-sm font-medium text-navy hover:underline"
                  >
                    {activeConsentDoc.file_name}
                  </button>
                  <span className="shrink-0 text-[11px] text-ink-muted">
                    {activeConsentDoc.version_label}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => openConsentDoc(activeConsentDoc.consent_document_id)}
                  disabled={viewConsentDocLoading}
                  className="shrink-0 text-xs font-medium text-navy transition-colors hover:underline"
                  aria-label={`View ${activeConsentDoc.file_name}`}
                >
                  {viewConsentDocLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    'View'
                  )}
                </button>
              </li>
            </ul>
          </div>
        )}
      </ReviewSection>

      {/* Final authorisation — locked until all 5 section checkboxes are ticked */}
      {(() => {
        const allSectionsDone =
          checks.step1 && checks.step2 && checks.step3 && checks.step4 && checks.icf;
        return (
          <div
            className={`rounded-md border p-4 transition-colors ${checks.consent ? 'border-emerald-300 bg-emerald-50/40' : allSectionsDone ? 'border-surface-border bg-white' : 'border-surface-border bg-surface-muted/40 opacity-60'}`}
          >
            <label
              className={`flex items-center gap-3 ${allSectionsDone ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            >
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-primary"
                checked={checks.consent}
                disabled={!allSectionsDone}
                onChange={(e) => onCheck('consent', e.target.checked)}
              />
              <span className="text-sm font-medium text-ink-primary">
                I have reviewed all the information above and authorise this study to proceed to
                Activation.
              </span>
            </label>
            {!allSectionsDone && (
              <p className="mt-2 text-xs text-ink-muted pl-7">
                Confirm all five sections above before authorising.
              </p>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BlindingSummary — the PI approves the blinding set-up too (Harish, 2 Oct 2026)
// ---------------------------------------------------------------------------

const BLINDING_DESIGN_LABELS: Record<StudyBlindingType, string> = {
  open_label: 'Open-label',
  single_blind: 'Single-blind',
  double_blind: 'Double-blind',
  triple_blind: 'Triple-blind',
};

function BlindingSummary({ study }: { study: OnboardingContext['study'] }) {
  const templatesQ = useQuery({
    queryKey: ['binderTemplates', 'active'],
    queryFn: () => listActiveBinderTemplates(),
    staleTime: 5 * 60_000,
  });
  const templateName = (id: string | null | undefined) =>
    id ? (templatesQ.data?.templates.find((t) => t.template_id === id)?.name ?? '—') : '—';
  const blinded = isBlindedStudyType(study.blinding_type ?? null);
  const dt = 'text-[11px] text-ink-muted uppercase tracking-wide';
  return (
    <>
      <div>
        <dt className={dt}>Blinding design</dt>
        <dd className="font-medium">{study.blinding_type ? BLINDING_DESIGN_LABELS[study.blinding_type] : 'Not set'}</dd>
      </div>
      <div>
        <dt className={dt}>Consent mode</dt>
        <dd className="font-medium">{study.consent_mode === 'paper' ? 'Paper only' : 'Electronic'}</dd>
      </div>
      <div>
        <dt className={dt}>{blinded ? 'Blinded binder template' : 'Study binder template'}</dt>
        <dd className="font-medium">{templateName(study.binder_template_id)}</dd>
      </div>
      {blinded && (
        <div>
          <dt className={dt}>Unblinded binder template</dt>
          <dd className="font-medium">{templateName(study.unblinded_binder_template_id)}</dd>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ReviewSection — collapsible step card with confirm checkbox
// ---------------------------------------------------------------------------

function ReviewSection({
  label,
  checked,
  onCheck,
  confirmText,
  children,
}: {
  label: string;
  checked: boolean;
  onCheck: (v: boolean) => void;
  confirmText: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`rounded-md border p-4 space-y-3 transition-colors ${checked ? 'border-emerald-300 bg-emerald-50/40' : 'border-surface-border bg-white'}`}
    >
      <div className="flex items-center gap-2">
        {checked ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
        ) : (
          <div className="h-4 w-4 shrink-0 rounded-full border-2 border-ink-muted" />
        )}
        <span className="text-sm font-semibold">{label}</span>
      </div>
      <div className="pl-6">{children}</div>
      <div className="pl-6 border-t border-surface-border/50 pt-3">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 shrink-0 accent-primary"
            checked={checked}
            onChange={(e) => onCheck(e.target.checked)}
          />
          <span className="text-xs text-ink-muted">{confirmText}</span>
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Activation (gate + Activate button)
// ---------------------------------------------------------------------------

function Step5Activation({ ctx }: { ctx: OnboardingContext }) {
  const isAlreadyActive = ctx.study_site.status === 'active';

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Activation</h2>
      <p className="text-sm text-ink-muted">
        Once all readiness flags are Ready, the coordinator can activate this study at your
        site. Activation is logged in the audit trail and the study appears as Active in the
        Portfolio.
      </p>

      <div className="card divide-y divide-surface-border">
        <ActivationRow label="Staff ready" ok={ctx.readiness.staff?.value === true} />
        <ActivationRow label="eConsent ready" ok={ctx.readiness.econsent?.value === true} />
        <ActivationRow label="eBinder / eISF ready" ok={ctx.readiness.ebinder?.value === true} />
        <ActivationRow label="eSource ready" ok={ctx.readiness.esource?.value === true} />
      </div>

      {isAlreadyActive && (
        <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> Study is Active at this site.
        </div>
      )}
    </div>
  );
}

function ActivationRow({ label, ok, hint }: { label: string; ok: boolean; hint?: string }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 text-sm">
      <span>
        {label}
        {hint && <span className="ml-2 text-[11px] text-ink-muted">{hint}</span>}
      </span>
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : (
        <Lock className="h-4 w-4 text-ink-muted" />
      )}
    </div>
  );
}
